import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { CheckCircle, XCircle, Clock } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState('loading')
  const [orderNumber, setOrderNumber] = useState('')
  const [error, setError] = useState('')
  const [countdown, setCountdown] = useState(5)

  useEffect(() => {
    const handlePaymentResult = async () => {
      try {
        // Handle both internal payment success format and UPayments redirect format
        const paymentId = searchParams.get('paymentId') || searchParams.get('payment_id')
        const paymentStatus = searchParams.get('result') || searchParams.get('status')
        const trackId = searchParams.get('track_id')
        const requestedOrderId = searchParams.get('requested_order_id')
        
        console.log('[PAYMENT SUCCESS] Payment ID:', paymentId)
        console.log('[PAYMENT SUCCESS] Payment Status:', paymentStatus)
        console.log('[PAYMENT SUCCESS] Track ID:', trackId)
        console.log('[PAYMENT SUCCESS] Requested Order ID:', requestedOrderId)
        
        // Handle UPayments payment results based on actual status
        if (requestedOrderId && paymentStatus) {
          if (paymentStatus === 'CAPTURED') {
            console.log('[PAYMENT SUCCESS] UPayments payment captured successfully')
            
            // For UPayments, the order already exists and payment is captured
            setOrderNumber(requestedOrderId)
            setStatus('success')
            
            // Clean up pending order
            localStorage.removeItem('pendingOrder')
            
            // Start countdown redirect to order status after 5 seconds
            let countdownTimer = 5
            setCountdown(countdownTimer)
            
            const countdownInterval = setInterval(() => {
              countdownTimer--
              setCountdown(countdownTimer)
              if (countdownTimer <= 0) {
                clearInterval(countdownInterval)
                navigate(`/order-status/${requestedOrderId}`)
              }
            }, 1000)
            
            return
          } else if (paymentStatus === 'FAILED') {
            console.log('[PAYMENT SUCCESS] UPayments payment failed')
            
            // Get pending order data from localStorage to restore cart
            const pendingOrder = localStorage.getItem('pendingOrder')
            
            setStatus('error')
            setError(`فشل الدفع للطلب ${requestedOrderId}. جاري إعادة التوجيه إلى صفحة الدفع...`)
            
            // Redirect back to checkout after 3 seconds
            setTimeout(() => {
              // The cart should still be preserved since the order was deleted
              // and we stored the cart data in localStorage
              navigate('/checkout')
            }, 3000)
            
            return
          } else {
            console.log('[PAYMENT SUCCESS] UPayments payment status:', paymentStatus)
            setStatus('pending')
            setOrderNumber(requestedOrderId)
            return
          }
        }
        
        // Handle UPayments successful payment (result=CAPTURED) - legacy check
        if (paymentStatus === 'CAPTURED' && requestedOrderId) {
          console.log('[PAYMENT SUCCESS] UPayments payment captured successfully')
          
          // For UPayments, the order already exists and payment is captured
          setOrderNumber(requestedOrderId)
          setStatus('success')
          
          // Clean up pending order
          localStorage.removeItem('pendingOrder')
          
          // Start countdown redirect to order status after 5 seconds
          let countdownTimer = 5
          setCountdown(countdownTimer)
          
          const countdownInterval = setInterval(() => {
            countdownTimer--
            setCountdown(countdownTimer)
            if (countdownTimer <= 0) {
              clearInterval(countdownInterval)
              navigate(`/order-status/${requestedOrderId}`)
            }
          }, 1000)
          
          return
        }
        
        // For other payment methods or fallback
        if (paymentId || requestedOrderId) {
          // If we have order ID, use it directly
          if (requestedOrderId) {
            setOrderNumber(requestedOrderId)
            setStatus('success')
            
            // Start countdown redirect
            let countdownTimer = 5
            setCountdown(countdownTimer)
            
            const countdownInterval = setInterval(() => {
              countdownTimer--
              setCountdown(countdownTimer)
              if (countdownTimer <= 0) {
                clearInterval(countdownInterval)
                navigate(`/order-status/${requestedOrderId}`)
              }
            }, 1000)
            
            return
          }
          
          // Handle other cases where we only have payment ID
          setStatus('pending')
        } else {
          setStatus('error')
          setError('معلومات الدفع غير موجودة')
        }
      } catch (error) {
        console.error('[PAYMENT SUCCESS] Error handling payment result:', error)
        setStatus('error')
        setError(error.message || 'فشل في معالجة الدفع')
      }
    }

    handlePaymentResult()
  }, [searchParams, navigate])

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" />
          <p className="mt-4 text-lg text-gray-600">جاري معالجة دفعتك...</p>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">تم الدفع بنجاح!</h1>
          <p className="text-gray-600 mb-4">
            تم معالجة دفعتك بنجاح.
          </p>
          {orderNumber && (
            <p className="text-sm text-gray-500 mb-6">
              رقم الطلب: <span className="font-semibold">{orderNumber}</span>
            </p>
          )}
          <p className="text-sm text-gray-500">
            سيتم إعادة توجيهك إلى صفحة حالة طلبك خلال {countdown} ثانية...
          </p>
          <button
            onClick={() => navigate(`/order-status/${orderNumber}`)}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            عرض الطلب الآن
          </button>
        </div>
      </div>
    )
  }

  if (status === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">الدفع قيد المراجعة</h1>
          <p className="text-gray-600 mb-6">
            جاري معالجة دفعتك. يرجى الانتظار بينما نؤكد دفعتك.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            فحص الحالة
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
        <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-4">فشل الدفع</h1>
        <p className="text-gray-600 mb-6">
          {error || 'حدثت مشكلة في معالجة دفعتك. يرجى المحاولة مرة أخرى.'}
        </p>
        <div className="space-y-2">
          <button
            onClick={() => navigate('/checkout')}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            حاول مرة أخرى
          </button>
          <button
            onClick={() => navigate('/cart')}
            className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
          >
            العودة إلى السلة
          </button>
        </div>
      </div>
    </div>
  )
}

export default PaymentSuccess
